import React, { useImperativeHandle, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';
import { t } from 'i18next';
import './BrandVisibilityBarChart.scss';
import { PieChartData } from '../../../models/common/PieChartData';
import { getChartThemeColors } from '../../../utils/chartTheme';
import { useTheme } from '../../../contexts/theme/ThemeContext';
import BrandAxisAvatars from '../brand-axis-avatars/BrandAxisAvatars';
import BrandIcon from '../../brand-icon/BrandIcon';
import useIsMobile from '../../../hooks/global/UseIsMobile';

interface BrandVisibilityBarChartProps {
  data: PieChartData[];
  brandColors: { name: string; color: string }[] | null;
  /** Podpis z liczba dni; pomijamy go w raporcie-snapshocie, ktory nie ma okresu. */
  daysCount?: number;
  /** The brand selected in the dropdown — always shown, even if outside the top 7. */
  selectedBrand?: string;
  /** Wystawia instancję ECharts na zewnątrz — raport zrzuca z niej wykres do PDF-a. */
  chartRef?: React.RefObject<ReactECharts>;
  /**
   * Wypisuje wartość nad słupkiem. Raport tego potrzebuje, bo jego PDF to zrzut TEGO wykresu —
   * bez etykiet czytelnik dokumentu nie ma skąd wziąć liczb (tooltipy działają tylko na ekranie).
   */
  showValueLabels?: boolean;
}

// How many brands to show at once.
const MAX_BARS = 7;
const FALLBACK_COLOR = '#c9ccd6';

// ECharts vertical bar chart of per-brand visibility — one bar per brand (top 7, the selected brand
// always included), colored by the brand's chart color. Brand names are ECharts axis labels; the
// avatar badges above them are the shared BrandAxisAvatars overlay.
const BrandVisibilityBarChart: React.FC<BrandVisibilityBarChartProps> = ({
  data,
  brandColors,
  daysCount,
  selectedBrand,
  chartRef,
  showValueLabels = false,
}) => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const chartTheme = getChartThemeColors();
  const eChartsRef = useRef<ReactECharts>(null);
  useImperativeHandle(chartRef, () => eChartsRef.current!, []);

  const colorByName = useMemo(
    () => new Map((brandColors ?? []).map((b) => [b.name, b.color])),
    [brandColors]
  );

  // Top 7 by visibility, but the selected (monitored) brand is always shown — if it falls outside the
  // top 7 it replaces the lowest of them.
  const bars = useMemo(() => {
    const sorted = [...data].sort((a, b) => b.value - a.value);
    const top = sorted.slice(0, MAX_BARS);
    if (selectedBrand && !top.some((b) => b.name === selectedBrand)) {
      const sel = sorted.find((b) => b.name === selectedBrand);
      if (sel) {
        top[top.length - 1] = sel;
        top.sort((a, b) => b.value - a.value);
      }
    }
    return top;
  }, [data, selectedBrand]);

  const avatarItems = useMemo(
    () => bars.map((b) => ({ name: b.name, color: colorByName.get(b.name) ?? FALLBACK_COLOR })),
    [bars, colorByName]
  );

  const option: EChartsOption = useMemo(() => {
    const categories = bars.map((b) => b.name);
    const seriesData = bars.map((b) => ({
      value: Math.round(b.value * 10) / 10,
      itemStyle: {
        color: colorByName.get(b.name) ?? FALLBACK_COLOR,
        borderRadius: [8, 8, 0, 0] as [number, number, number, number],
      },
    }));
    const maxValue = Math.max(1, ...bars.map((b) => b.value));
    // ~15% headroom above the tallest bar (/0.85), but visibility is a percentage — never let the
    // headroom push the axis past 100% (e.g. a 100% bar would otherwise top the axis at 118%).
    const yMax = Math.min(100, Math.ceil(maxValue / 0.85));

    return {
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        borderColor: chartTheme.tooltipBorder,
        backgroundColor: chartTheme.tooltipBg,
        textStyle: { color: chartTheme.tooltipText },
        valueFormatter: (value: unknown) => `${value ?? ''}%`,
      },
      grid: { top: 14, bottom: 76, left: 40, right: 10 },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          interval: 0,
          margin: 42, // pushes the name below the overlaid avatar
          color: chartTheme.axisLabel,
          fontSize: 11,
          width: 74,
          overflow: 'truncate',
        },
      },
      yAxis: {
        type: 'value',
        max: yMax,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { type: 'dashed', color: chartTheme.splitLine } },
        axisLabel: { color: chartTheme.axisLabel, formatter: '{value}%' },
      },
      series: [
        {
          type: 'bar',
          data: seriesData,
          barWidth: 42,
          barMaxWidth: 52,
          label: {
            show: showValueLabels,
            position: 'top',
            formatter: '{c}%',
            fontFamily: 'Inter,sans-serif',
            fontSize: 11,
            fontWeight: 600,
            color: chartTheme.tooltipText,
          },
        },
      ],
    };
  }, [bars, colorByName, chartTheme, showValueLabels]);

  const footer = daysCount === undefined ? '' : t('showingDataForDays', { count: daysCount });

  // Telefon: marki jedna pod drugą z poziomym paskiem — siedem pionowych słupków z nazwami i ikonami
  // nachodziło na siebie. Bez `chartRef`, bo raport zrzuca do PDF-a instancję ECharts.
  if (isMobile && !chartRef) {
    const maxValue = Math.max(1, ...bars.map((b) => b.value));
    return (
      <div className="brand-visibility-bars brand-visibility-bars--list">
        {bars.map((b) => {
          const color = colorByName.get(b.name) ?? FALLBACK_COLOR;
          return (
            <div key={b.name} className="brand-visibility-bars__row">
              <BrandIcon name={b.name} color={color} size="sm" />
              <span className="brand-visibility-bars__name">{b.name}</span>
              <span className="brand-visibility-bars__track">
                <span
                  className="brand-visibility-bars__fill"
                  style={{ width: `${(b.value / maxValue) * 100}%`, backgroundColor: color }}
                />
              </span>
              <span className="brand-visibility-bars__value">{Math.round(b.value * 10) / 10}%</span>
            </div>
          );
        })}
        <div className="brand-visibility-bars__footer">{footer}</div>
      </div>
    );
  }

  return (
    <div className="brand-visibility-bars">
      {/* Keyed together: a theme switch remounts the chart, and the overlay must re-find the new DOM. */}
      <React.Fragment key={theme}>
        <ReactECharts
          ref={eChartsRef}
          option={option}
          notMerge
          style={{ height: '100%', width: '100%' }}
        />
        <BrandAxisAvatars chartRef={eChartsRef} items={avatarItems} size="md" />
      </React.Fragment>
      <div className="brand-visibility-bars__footer">{footer}</div>
    </div>
  );
};

export default BrandVisibilityBarChart;
